import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { NavigationLink } from '../navigationConfig';

interface TopNavigationProps {
  isAuthenticated: boolean;
  displayUserName: string;
  organizationDisplayName: string;
  showFullNavigation: boolean;
  showUserSettingsMenu: boolean;
  visibleSettingsLinks: NavigationLink[];
  homePath: string;
  orgAwarePath: (path: string) => string;
  guestOrgBasePath: string;
  restrictToLearnerMenu: boolean;
  onLogin: () => void;
  onLogout: () => void;
}

const TopNavigation: React.FC<TopNavigationProps> = ({
  isAuthenticated,
  displayUserName,
  organizationDisplayName,
  showFullNavigation,
  showUserSettingsMenu,
  visibleSettingsLinks,
  homePath,
  orgAwarePath,
  guestOrgBasePath,
  restrictToLearnerMenu,
  onLogin,
  onLogout,
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [howItWorksMenuOpen, setHowItWorksMenuOpen] = useState(false);
  const hasOrganizationBranding = isAuthenticated && organizationDisplayName.trim().length > 0;
  const brandingTitle = hasOrganizationBranding ? organizationDisplayName : 'Exam Masters';
  const guestCoursesPath = `${guestOrgBasePath}/courses`;
  const guestPracticePath = `${guestOrgBasePath}/practice`;
  const guestPracticeTestsPath = `${guestOrgBasePath}/practice-tests`;
  const guestDashboardPath = `${guestOrgBasePath}/analytics`;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setMenuOpen(false);
        setUserMenuOpen(false);
        setHowItWorksMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const menu = document.getElementById('mobile-menu');
      const hamburger = document.getElementById('hamburger-button');
      const userMenu = document.getElementById('user-menu-dropdown');
      const userButton = document.getElementById('user-menu-button');
      const howItWorksMenu = document.getElementById('how-it-works-menu-dropdown');
      const howItWorksButton = document.getElementById('how-it-works-menu-button');

      if (menuOpen && menu && hamburger && !menu.contains(target) && !hamburger.contains(target)) {
        setMenuOpen(false);
      }

      if (userMenuOpen && userMenu && userButton && !userMenu.contains(target) && !userButton.contains(target)) {
        setUserMenuOpen(false);
      }

      if (howItWorksMenuOpen && howItWorksMenu && howItWorksButton && !howItWorksMenu.contains(target) && !howItWorksButton.contains(target)) {
        setHowItWorksMenuOpen(false);
      }
    };

    if (menuOpen || userMenuOpen || howItWorksMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen, userMenuOpen, howItWorksMenuOpen]);

  return (
    <header
      style={{
        background: '#222',
        color: '#fff',
        padding: '0 0 0 0',
        marginBottom: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 12px',
          height: 64,
        }}
      >
        <Link to={homePath} style={{ display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap', color: '#fff', textDecoration: 'none' }}>
          <img
            src="/Icon-ExamMasters2.png"
            alt="Exam Masters"
            style={{
              width: isMobile ? 24 : 30,
              height: isMobile ? 24 : 30,
              borderRadius: 6,
              objectFit: 'cover',
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span
              style={{
                fontWeight: 700,
                fontSize: isMobile ? 18 : 24,
                letterSpacing: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: isMobile ? 180 : 280,
              }}
              title={brandingTitle}
            >
              {brandingTitle}
            </span>
            {hasOrganizationBranding && (
              <span style={{ fontSize: isMobile ? 10 : 11, opacity: 0.8, letterSpacing: 0.4 }}>
                Exam Masters
              </span>
            )}
          </div>
        </Link>

        {isMobile ? (
          <>
            <button
              id="hamburger-button"
              onClick={() => setMenuOpen(!menuOpen)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                fontSize: 28,
                cursor: 'pointer',
                padding: 8,
              }}
            >
              ☰
            </button>

            {menuOpen && (
              <div
                id="mobile-menu"
                style={{
                  position: 'absolute',
                  top: 64,
                  left: 0,
                  background: '#222',
                  width: '100%',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  zIndex: 1000,
                  padding: '12px 0',
                }}
              >
                {isAuthenticated && (
                  <Link to={orgAwarePath('/analytics')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>
                    Dashboard
                  </Link>
                )}

                {showFullNavigation && (
                  <>
                    <Link to={orgAwarePath('/courses')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>
                      Courses
                    </Link>
                    <Link to={orgAwarePath('/practice')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>
                      Questions
                    </Link>
                    <Link to={orgAwarePath('/practice-tests')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>
                      Practice Tests
                    </Link>
                    {!restrictToLearnerMenu && (
                      <>
                        <Link to={orgAwarePath('/blogs')} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>
                          Blogs
                        </Link>
                        <button
                          id="how-it-works-menu-button"
                          type="button"
                          onClick={() => setHowItWorksMenuOpen((previous) => !previous)}
                          style={{ display: 'block', width: '100%', background: 'transparent', border: 'none', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left', cursor: 'pointer' }}
                        >
                          How It Works {howItWorksMenuOpen ? '▲' : '▼'}
                        </button>
                        {howItWorksMenuOpen && (
                          <div id="how-it-works-menu-dropdown" style={{ paddingBottom: 8 }}>
                            <Link to="/how-it-works" onClick={() => { setMenuOpen(false); setHowItWorksMenuOpen(false); }} style={{ display: 'block', color: '#d1d5db', textDecoration: 'none', padding: '8px 40px', fontSize: 15, textAlign: 'left' }}>
                              Overview
                            </Link>
                            <Link to="/what-we-do" onClick={() => { setMenuOpen(false); setHowItWorksMenuOpen(false); }} style={{ display: 'block', color: '#d1d5db', textDecoration: 'none', padding: '8px 40px', fontSize: 15, textAlign: 'left' }}>
                              What We Do
                            </Link>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {!isAuthenticated && (
                  <>
                    <Link to={guestDashboardPath} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>
                      Dashboard
                    </Link>
                    <Link to={guestCoursesPath} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>
                      Courses
                    </Link>
                    <Link to={guestPracticePath} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>
                      Questions
                    </Link>
                    <Link to={guestPracticeTestsPath} onClick={() => setMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left' }}>
                      Practice Tests
                    </Link>
                    {!restrictToLearnerMenu && (
                      <>
                        <button
                          id="how-it-works-menu-button"
                          type="button"
                          onClick={() => setHowItWorksMenuOpen((previous) => !previous)}
                          style={{ display: 'block', width: '100%', background: 'transparent', border: 'none', color: '#fff', textDecoration: 'none', padding: '12px 24px', fontSize: 16, textAlign: 'left', cursor: 'pointer' }}
                        >
                          How It Works {howItWorksMenuOpen ? '▲' : '▼'}
                        </button>
                        {howItWorksMenuOpen && (
                          <div id="how-it-works-menu-dropdown" style={{ paddingBottom: 8 }}>
                            <Link to="/how-it-works" onClick={() => { setMenuOpen(false); setHowItWorksMenuOpen(false); }} style={{ display: 'block', color: '#d1d5db', textDecoration: 'none', padding: '8px 40px', fontSize: 15, textAlign: 'left' }}>
                              Overview
                            </Link>
                            <Link to="/what-we-do" onClick={() => { setMenuOpen(false); setHowItWorksMenuOpen(false); }} style={{ display: 'block', color: '#d1d5db', textDecoration: 'none', padding: '8px 40px', fontSize: 15, textAlign: 'left' }}>
                              What We Do
                            </Link>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {isAuthenticated && (
                  <div style={{ padding: '12px 24px' }}>
                    <div style={{ position: 'relative' }}>
                      <button
                        id="user-menu-button"
                        type="button"
                        onClick={() => setUserMenuOpen((previous) => !previous)}
                        style={{
                          background: 'transparent',
                          color: '#fff',
                          border: '1px solid rgba(255,255,255,0.25)',
                          borderRadius: 4,
                          padding: '8px 12px',
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: 600,
                          width: '100%',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                        }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayUserName}</span>
                        <span>{userMenuOpen ? '▲' : '▼'}</span>
                      </button>

                      {userMenuOpen && (
                        <div id="user-menu-dropdown" style={{ marginTop: 8, backgroundColor: '#333', borderRadius: 4, padding: 8 }}>
                          {showUserSettingsMenu && visibleSettingsLinks.map((link) => (
                            <Link
                              key={link.to}
                              to={link.to}
                              onClick={() => {
                                setMenuOpen(false);
                                setUserMenuOpen(false);
                              }}
                              style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '10px 12px', fontSize: 14, textAlign: 'left', width: '100%' }}
                            >
                              {link.label}
                            </Link>
                          ))}

                          <button
                            type="button"
                            onClick={() => {
                              setMenuOpen(false);
                              setUserMenuOpen(false);
                              onLogout();
                            }}
                            style={{
                              background: '#ef4444',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              padding: '8px 12px',
                              cursor: 'pointer',
                              fontSize: 14,
                              fontWeight: 600,
                              width: '100%',
                              textAlign: 'left',
                              marginTop: 8,
                            }}
                          >
                            Logout
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!isAuthenticated && (
                  <div style={{ padding: '12px 24px' }}>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        onLogin();
                      }}
                      style={{
                        background: '#2563eb',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        padding: '8px 16px',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 600,
                        width: '100%',
                        textAlign: 'left',
                      }}
                    >
                      Login
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <nav style={{ display: 'flex', alignItems: 'center' }}>
            {isAuthenticated && (
              <Link to={orgAwarePath('/analytics')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>
                Dashboard
              </Link>
            )}

            {showFullNavigation && (
              <>
                <Link to={orgAwarePath('/courses')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>
                  Courses
                </Link>
                <Link to={orgAwarePath('/practice')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>
                  Questions
                </Link>
                <Link to={orgAwarePath('/practice-tests')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>
                  Practice Tests
                </Link>
                {!restrictToLearnerMenu && (
                  <>
                    <Link to={orgAwarePath('/blogs')} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>
                      Blogs
                    </Link>
                    <div style={{ position: 'relative', marginRight: 24 }}>
                      <button
                        id="how-it-works-menu-button"
                        type="button"
                        onClick={() => setHowItWorksMenuOpen((previous) => !previous)}
                        style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, padding: 0 }}
                      >
                        How It Works {howItWorksMenuOpen ? '▲' : '▼'}
                      </button>
                      {howItWorksMenuOpen && (
                        <div
                          id="how-it-works-menu-dropdown"
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 8px)',
                            left: 0,
                            backgroundColor: '#333',
                            minWidth: 170,
                            borderRadius: 4,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            zIndex: 1000,
                            padding: 8,
                          }}
                        >
                          <Link to="/how-it-works" onClick={() => setHowItWorksMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '8px 10px', fontSize: 14 }}>
                            Overview
                          </Link>
                          <Link to="/what-we-do" onClick={() => setHowItWorksMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '8px 10px', fontSize: 14 }}>
                            What We Do
                          </Link>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            {!isAuthenticated && (
              <>
                <Link to={guestDashboardPath} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>
                  Dashboard
                </Link>
                <Link to={guestCoursesPath} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>
                  Courses
                </Link>
                <Link to={guestPracticePath} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>
                  Questions
                </Link>
                <Link to={guestPracticeTestsPath} style={{ color: '#fff', textDecoration: 'none', marginRight: 24, fontSize: 16 }}>
                  Practice Tests
                </Link>
                {!restrictToLearnerMenu && (
                  <div style={{ position: 'relative', marginRight: 24 }}>
                    <button
                      id="how-it-works-menu-button"
                      type="button"
                      onClick={() => setHowItWorksMenuOpen((previous) => !previous)}
                      style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, padding: 0 }}
                    >
                      How It Works {howItWorksMenuOpen ? '▲' : '▼'}
                    </button>
                    {howItWorksMenuOpen && (
                      <div
                        id="how-it-works-menu-dropdown"
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 8px)',
                          left: 0,
                          backgroundColor: '#333',
                          minWidth: 170,
                          borderRadius: 4,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                          zIndex: 1000,
                          padding: 8,
                        }}
                      >
                        <Link to="/how-it-works" onClick={() => setHowItWorksMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '8px 10px', fontSize: 14 }}>
                          Overview
                        </Link>
                        <Link to="/what-we-do" onClick={() => setHowItWorksMenuOpen(false)} style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '8px 10px', fontSize: 14 }}>
                          What We Do
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {isAuthenticated && (
              <div style={{ position: 'relative', marginLeft: 8 }}>
                <button
                  id="user-menu-button"
                  type="button"
                  onClick={() => setUserMenuOpen((previous) => !previous)}
                  style={{
                    background: 'transparent',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.25)',
                    borderRadius: 4,
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ whiteSpace: 'nowrap' }}>{displayUserName}</span>
                  <span>{userMenuOpen ? '▲' : '▼'}</span>
                </button>

                {userMenuOpen && (
                  <div
                    id="user-menu-dropdown"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      right: 0,
                      backgroundColor: '#333',
                      minWidth: '200px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      borderRadius: '4px',
                      zIndex: 1000,
                      padding: '8px',
                    }}
                  >
                    {showUserSettingsMenu && visibleSettingsLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setUserMenuOpen(false)}
                        style={{ display: 'block', color: '#fff', textDecoration: 'none', padding: '10px 12px', fontSize: 14, whiteSpace: 'nowrap', textAlign: 'left', width: '100%' }}
                      >
                        {link.label}
                      </Link>
                    ))}

                    <button
                      type="button"
                      onClick={() => {
                        setUserMenuOpen(false);
                        onLogout();
                      }}
                      style={{
                        background: '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 600,
                        width: '100%',
                        textAlign: 'left',
                        marginTop: 8,
                      }}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}

            {!isAuthenticated && (
              <button
                onClick={onLogin}
                style={{
                  background: '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 20px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Login
              </button>
            )}
          </nav>
        )}
      </div>
    </header>
  );
};

export default TopNavigation;